import React, { useEffect, useState, useMemo } from 'react';
import styled from 'styled-components';
import { getHiveActivityLogs, deleteHiveActivityLog, getAllHiveActivityLogs } from '../../../../../../services/apiaryService';
import close from '../../../assets/images/close.png';
import * as Styles from '../../../Farmers/components/FarmerForm/style';
import { Row, Col, Button, Form } from 'react-bootstrap';
import LoadingOverlay from 'react-loading-overlay';
import BounceLoader from 'react-spinners/BounceLoader';
import BootstrapTable from 'react-bootstrap-table-next';
import paginationFactory from 'react-bootstrap-table2-paginator';
import { Select, DatePicker, Empty } from 'antd';
import moment from 'moment';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import 'react-bootstrap-table-next/dist/react-bootstrap-table2.min.css';
import 'react-bootstrap-table2-paginator/dist/react-bootstrap-table2-paginator.min.css';
import GenericExportCSVModal from '../../../shared/GenericExportCSVModal';

const { Option } = Select;

// --- Helper Functions ---
const resolveImageUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    
    const baseUrl = (process.env.REACT_APP_API_APP_FIELD_URL || '').replace(/\/$/, '');
    const cleanUrl = url.startsWith('/') ? url : '/' + url;
    
    return baseUrl + cleanUrl;
};

/**
 * Safely parses stringified arrays and outputs a flat array of clean string items.
 */
const parseArrayField = (val) => {
    if (!val) return [];
    
    // Helper to recursively parse a value if it's a JSON string representing an array
    const resolveValue = (v) => {
        if (typeof v === 'string' && v.trim().startsWith('[')) {
            try {
                const parsed = JSON.parse(v);
                return resolveValue(parsed);
            } catch (e) {
                // If parsing fails, clean up quotes and brackets manually as fallback
                const cleaned = v.replace(/[\[\]"]/g, '').trim();
                return cleaned ? [cleaned] : [];
            }
        }
        if (Array.isArray(v)) {
            return v.flatMap(item => resolveValue(item));
        }
        return [v];
    };

    const flatResult = resolveValue(val);
    // Filter out empty/null/undefined/empty-string values and ignore noise like '[""]' or '[]'
    return flatResult
        .map(x => typeof x === 'string' ? x.trim() : String(x))
        .filter(x => x && x !== 'null' && x !== 'undefined' && x !== 'N/A' && x !== '[""]' && x !== '[]');
};

// --- Styled Components ---
const MainContainer = styled.div`
    padding: ${props => props.isGlobal ? '30px' : '20px 0'};
    background: ${props => props.isGlobal ? '#f8f9fa' : 'transparent'};
    min-height: ${props => props.isGlobal ? '100vh' : 'auto'};
    margin-left: ${props => props.isGlobal ? '15%' : '0'};
    width: ${props => props.isGlobal ? '85%' : '100%'};

    @media (max-width: 500px) {
        margin-left: 0;
        width: 100%;
        padding: 15px;
    }
`;

const HeaderSection = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 24px;
`;

const FilterCard = styled.div`
    background: #f8f9fa;
    padding: 20px;
    border-radius: 12px;
    margin-bottom: 30px;
    border: 1px solid #e9ecef;
`;

const TableWrapper = styled.div`
    background: #fff;
    border-radius: 12px;
    box-shadow: 0 4px 12px rgba(0,0,0,0.03);
    overflow: hidden;
    border: 1px solid #eaeaea;

    .modern-table thead th {
        background: #f8f9fa;
        color: #636e72;
        font-weight: 700;
        font-size: 13px;
        padding: 15px;
        text-transform: uppercase;
        border-bottom: 2px solid #ff8701 !important;
    }

    .modern-table tbody td {
        padding: 15px;
        vertical-align: middle;
        font-size: 14px;
    }
`;

const StatusBadge = styled.span`
    display: inline-block;
    padding: 4px 10px;
    border-radius: 20px;
    font-size: 11px;
    font-weight: 700;
    margin: 2px;
    background-color: ${props => {
        if (props.type === 'success' || props.type === 'YES' || props.type === 'GOOD' || props.type === 'EXCELLENT') return '#e6fffa';
        if (props.type === 'warning' || props.type === 'AVERAGE') return '#fff9db';
        return '#fff5f5'; // error, NO, BAD
    }};
    color: ${props => {
        if (props.type === 'success' || props.type === 'YES' || props.type === 'GOOD' || props.type === 'EXCELLENT') return '#00b894';
        if (props.type === 'warning' || props.type === 'AVERAGE') return '#f59f00';
        return '#d63031'; // error, NO, BAD
    }};
    border: 1px solid ${props => {
        if (props.type === 'success' || props.type === 'YES' || props.type === 'GOOD' || props.type === 'EXCELLENT') return '#b2f2bb';
        if (props.type === 'warning' || props.type === 'AVERAGE') return '#ffe066';
        return '#ffa8a8'; // error, NO, BAD
    }};
`;

const DetailItem = styled.div`
    margin-bottom: 12px;
    font-size: 14px;
    color: #2d3436;
    span {
        font-weight: bold;
    }
`;

const PhotoGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
    gap: 15px;
    margin-top: 15px;
`;

const GeotaggedPhoto = styled.div`
    position: relative;
    width: 100%;
    aspect-ratio: 4/3;
    border-radius: 8px;
    overflow: hidden;
    background: #f0f0f0;
    box-shadow: 0 2px 8px rgba(0,0,0,0.1);
    cursor: pointer;
    transition: transform 0.2s;

    &:hover {
        transform: scale(1.02);
    }

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
    }
`;

const PhotoOverlay = styled.div`
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    background: linear-gradient(transparent, rgba(0,0,0,0.8));
    color: white;
    padding: 8px;
    font-size: 10px;
    line-height: 1.2;

    .geo-data {
        display: flex;
        align-items: center;
        gap: 5px;
        margin-bottom: 2px;
    }
`;

const ActivityCategoryTitle = styled.h5`
    color: #2d3436;
    font-weight: 700;
    margin: 15px 0 8px 0;
    font-size: 13px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
`;

// --- Detail Modal Component ---
const ActivityLogDetailModal = ({ onClose, data }) => {
    if (!data) return null;

    const hasPhotos = data.mediaUrls && data.mediaUrls.length > 0;
    
    // Group categories helper
    const renderBadges = (group, isRec = false) => {
        if (!group) return <span style={{ color: '#aaa', fontSize: 13, fontStyle: 'italic' }}>None Logged</span>;
        
        const badgesList = [];
        Object.entries(group).forEach(([category, items]) => {
            const parsedItems = parseArrayField(items);
            if (parsedItems.length > 0) {
                parsedItems.forEach((item, index) => {
                    const displayName = category
                        .replace('activities_recommended_', '')
                        .replace(/_|-/g, ' ')
                        .toUpperCase();
                    badgesList.push(
                        <StatusBadge key={`${category}-${index}`} type={isRec ? 'warning' : 'success'}>
                            <strong>{displayName}:</strong> {item}
                        </StatusBadge>
                    );
                });
            }
        });

        return badgesList.length > 0 ? badgesList : <span style={{ color: '#aaa', fontSize: 13, fontStyle: 'italic' }}>None Logged</span>;
    };

    const cleanPests = parseArrayField(data.observation?.pests_list);
    const cleanDiseases = parseArrayField(data.observation?.diseases_list);
    const role = data.added_by_user_type || 'beekeeper';
    const isBeeMitra = role.toLowerCase() === 'bee_mitra';

    return (
        <Styles.ModalWrapper style={{ zIndex: 1060 }}>
            <Styles.ModalContent2 style={{ maxWidth: '850px', width: '92%' }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "20px 20px 0 20px" }}>
                    <h3 style={{ color: '#ff8701', margin: 0 }}>Hive Activity Log Details</h3>
                    <img src={close} alt="Close" onClick={onClose} style={{ width: 20, cursor: "pointer", opacity: 0.6 }} />
                </div>
                
                <div style={{ padding: 20, maxHeight: '82vh', overflowY: 'auto' }}>
                    
                    {/* Metadata Card */}
                    <div style={{ background: '#f9f9f9', padding: 15, borderRadius: 8, marginBottom: 20, border: '1px solid #eee' }}>
                        <Row>
                            <Col md={6}>
                                <DetailItem><span>Visit Date:</span> {moment(data.created_at).format('DD MMM YYYY, hh:mm A')}</DetailItem>
                                <DetailItem>
                                    <span>Last Visited By:</span> {data.visited_by_name || data.userName?.name || 'Unknown'}
                                    <span style={{ 
                                        marginLeft: '10px', 
                                        fontSize: '11px', 
                                        padding: '2px 8px', 
                                        borderRadius: '4px',
                                        background: isBeeMitra ? '#f3e8ff' : '#ffe8d6',
                                        color: isBeeMitra ? '#7c3aed' : '#d97706',
                                        fontWeight: 'bold'
                                    }}>
                                        {isBeeMitra ? 'BEE MITRA' : 'BEEKEEPER'}
                                    </span>
                                </DetailItem>
                                {data.apiaryName && <DetailItem><span>Apiary:</span> {data.apiaryName}</DetailItem>}
                            </Col>
                            <Col md={6}>
                                <DetailItem><span>Latitude:</span> {data.latitude || 'N/A'}</DetailItem>
                                <DetailItem><span>Longitude:</span> {data.longitude || 'N/A'}</DetailItem>
                            </Col>
                        </Row>
                    </div>

                    {/* Hive Metrics Grid */}
                    <h4 style={{ borderBottom: '1px solid #eee', paddingBottom: 8, marginBottom: 15 }}>Hive Colonies Metrics</h4>
                    <Row style={{ marginBottom: 20 }}>
                        <Col md={3} xs={6} style={{ marginBottom: 10 }}>
                            <div style={{ textAlign: 'center', padding: 12, background: '#e6fffa', borderRadius: 8, border: '1px solid #b2f2bb' }}>
                                <h2 style={{ margin: 0, color: '#00b894' }}>{data.observation?.number_of_strong_colonies ?? 0}</h2>
                                <p style={{ margin: 0, fontSize: 11, fontWeight: 700, color: '#2d3436' }}>Strong Colonies</p>
                            </div>
                        </Col>
                        <Col md={3} xs={6} style={{ marginBottom: 10 }}>
                            <div style={{ textAlign: 'center', padding: 12, background: '#fff9db', borderRadius: 8, border: '1px solid #ffe066' }}>
                                <h2 style={{ margin: 0, color: '#f59f00' }}>{data.observation?.number_of_weak_colonies ?? 0}</h2>
                                <p style={{ margin: 0, fontSize: 11, fontWeight: 700, color: '#2d3436' }}>Weak Colonies</p>
                            </div>
                        </Col>
                        <Col md={3} xs={6} style={{ marginBottom: 10 }}>
                            <div style={{ textAlign: 'center', padding: 12, background: '#fff5f5', borderRadius: 8, border: '1px solid #ffa8a8' }}>
                                <h2 style={{ margin: 0, color: '#d63031' }}>{data.observation?.number_of_colonies_without_queen ?? 0}</h2>
                                <p style={{ margin: 0, fontSize: 11, fontWeight: 700, color: '#2d3436' }}>Queenless Colonies</p>
                            </div>
                        </Col>
                        <Col md={3} xs={6} style={{ marginBottom: 10 }}>
                            <div style={{ textAlign: 'center', padding: 12, background: '#f8f9fa', borderRadius: 8, border: '1px solid #e9ecef' }}>
                                <h2 style={{ margin: 0, color: '#636e72' }}>{data.observation?.number_of_empty_hives ?? 0}</h2>
                                <p style={{ margin: 0, fontSize: 11, fontWeight: 700, color: '#2d3436' }}>Empty Hives</p>
                            </div>
                        </Col>
                    </Row>

                    {/* Observations */}
                    <h4 style={{ borderBottom: '1px solid #eee', paddingBottom: 8, marginBottom: 15 }}>General Observations</h4>
                    <Row style={{ marginBottom: 20 }}>
                        <Col md={6}>
                            <DetailItem><span>Cleanliness of Hives:</span> <StatusBadge type={data.observation?.cleanliness_of_hives}>{data.observation?.cleanliness_of_hives || 'N/A'}</StatusBadge></DetailItem>
                            <DetailItem><span>Condition of Beeboxes:</span> <StatusBadge type={data.observation?.condition_of_beeboxes}>{data.observation?.condition_of_beeboxes || 'N/A'}</StatusBadge></DetailItem>
                            <DetailItem><span>Shade Protection:</span> <StatusBadge type={data.observation?.shade_protection}>{data.observation?.shade_protection || 'N/A'}</StatusBadge></DetailItem>
                        </Col>
                        <Col md={6}>
                            <DetailItem><span>Flowers Availability:</span> <StatusBadge type={data.observation?.availability_of_flowers}>{data.observation?.availability_of_flowers || 'N/A'}</StatusBadge></DetailItem>
                            <DetailItem><span>Nectar Availability:</span> <StatusBadge type={data.observation?.availability_of_nectar}>{data.observation?.availability_of_nectar || 'N/A'}</StatusBadge></DetailItem>
                            <DetailItem><span>Pollen Availability:</span> <StatusBadge type={data.observation?.availability_of_pollen}>{data.observation?.availability_of_pollen || 'N/A'}</StatusBadge></DetailItem>
                        </Col>
                    </Row>

                    {/* Health Log Details */}
                    <h4 style={{ borderBottom: '1px solid #eee', paddingBottom: 8, marginBottom: 15 }}>Colony Health & Parasites</h4>
                    <Row style={{ marginBottom: 20 }}>
                        <Col md={6}>
                            <DetailItem>
                                <span>Pests Alert ({data.observation?.number_of_colonies_with_pests || 0} Colonies):</span> 
                                <div style={{ marginTop: 5 }}>
                                    {cleanPests.length > 0 ? (
                                        cleanPests.map((pest, i) => <StatusBadge key={i} type="error">{pest}</StatusBadge>)
                                    ) : <span style={{ color: '#aaa', fontStyle: 'italic', fontSize: 13 }}>No Pests Reported</span>}
                                </div>
                            </DetailItem>
                        </Col>
                        <Col md={6}>
                            <DetailItem>
                                <span>Diseases Alert ({data.observation?.number_of_colonies_with_diseases || 0} Colonies):</span>
                                <div style={{ marginTop: 5 }}>
                                    {cleanDiseases.length > 0 ? (
                                        cleanDiseases.map((disease, i) => <StatusBadge key={i} type="error">{disease}</StatusBadge>)
                                    ) : <span style={{ color: '#aaa', fontStyle: 'italic', fontSize: 13 }}>No Diseases Reported</span>}
                                </div>
                            </DetailItem>
                        </Col>
                    </Row>

                    {/* Activities performed and recommended */}
                    <Row style={{ marginBottom: 20 }}>
                        <Col md={6}>
                            <h4 style={{ borderBottom: '1px solid #eee', paddingBottom: 8, marginBottom: 15 }}>Activities Performed</h4>
                            <div style={{ background: '#f8f9fa', padding: 12, borderRadius: 8, minHeight: '100px' }}>
                                {renderBadges(data.activities_performed_during_visit)}
                            </div>
                        </Col>
                        <Col md={6}>
                            <h4 style={{ borderBottom: '1px solid #eee', paddingBottom: 8, marginBottom: 15 }}>Activities to be Performed</h4>
                            <div style={{ background: '#fffaf0', padding: 12, borderRadius: 8, minHeight: '100px', border: '1px dashed #ffe8d6' }}>
                                {renderBadges(data.activities_recommended_for_beekeeper, true)}
                            </div>
                        </Col>
                    </Row>

                    {/* Image gallery */}
                    <div style={{ marginTop: 25 }}>
                        <h4 style={{ borderBottom: '1px solid #eee', paddingBottom: 8, marginBottom: 15 }}>Inspection Media Uploads</h4>
                        {hasPhotos ? (
                            <PhotoGrid>
                                {data.mediaUrls.map((photoUrl, index) => {
                                    const fullUrl = resolveImageUrl(photoUrl);
                                    return (
                                        <GeotaggedPhoto key={index} onClick={() => window.open(fullUrl, '_blank')}>
                                            <img src={fullUrl} alt={`Activity Log ${index + 1}`} onError={(e) => { e.target.src = 'https://placehold.co/400x300?text=Image+Load+Failed'; }} />
                                            <PhotoOverlay>
                                                <div className="geo-data">
                                                    <i className="fa fa-map-marker"></i>
                                                    {data.latitude?.toFixed(6) || 'N/A'}, {data.longitude?.toFixed(6) || 'N/A'}
                                                </div>
                                            </PhotoOverlay>
                                        </GeotaggedPhoto>
                                    );
                                })}
                            </PhotoGrid>
                        ) : (
                            <div style={{ padding: '30px', textAlign: 'center', background: '#f8f9fa', borderRadius: 8, color: '#999' }}>
                                <i className="fa fa-picture-o fa-2x mb-2" style={{ opacity: 0.3 }}></i>
                                <p style={{ margin: 0 }}>No media files uploaded for this visit.</p>
                            </div>
                        )}
                    </div>
                </div>
            </Styles.ModalContent2>
        </Styles.ModalWrapper>
    );
};

// --- Main Table component ---
export default function NectarHiveActivityLogs({ apiaryId }) {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedLog, setSelectedLog] = useState(null);
    const [showExportModal, setShowExportModal] = useState(false);

    // Filter state
    const [quickFilter, setQuickFilter] = useState('all');
    const [dateRange, setDateRange] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');

    const fetchLogs = async () => {
        setLoading(true);
        try {
            const data = apiaryId ? await getHiveActivityLogs(apiaryId) : await getAllHiveActivityLogs();
            // Ignore items with isDeleted: true
            const activeLogs = (data || []).filter(item => !item.isDeleted);
            setLogs(activeLogs);
        } catch (error) {
            console.error("Failed fetching hive activities:", error);
            setLogs([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchLogs();
    }, [apiaryId]);

    const handleDelete = async (row) => {
        const result = await Swal.fire({
            title: 'Delete Activity Log?',
            text: 'This log will be removed from the audit records.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d63031',
            confirmButtonText: 'Yes, Delete',
            cancelButtonText: 'Cancel'
        });

        if (!result.isConfirmed) return;

        try {
            await deleteHiveActivityLog(row._id);
            toast.success("Activity log deleted successfully");
            fetchLogs(); // reload logs
        } catch (error) {
            toast.error(error.response?.data?.message || "Failed to delete log");
        }
    };

    const filteredLogs = useMemo(() => {
        return logs.filter(log => {
            // 1. Quick Selector Filter
            if (quickFilter === 'queenless' && !(log.observation?.number_of_colonies_without_queen > 0)) return false;
            if (quickFilter === 'pests' && !(log.observation?.number_of_colonies_with_pests > 0)) return false;
            if (quickFilter === 'diseases' && !(log.observation?.number_of_colonies_with_diseases > 0)) return false;

            // 2. Date Range Filter
            if (dateRange && dateRange[0] && dateRange[1]) {
                const logDate = moment(log.created_at);
                if (!logDate.isBetween(dateRange[0], dateRange[1], 'day', '[]')) return false;
            }

            // 3. Visited By Search Filter
            if (searchTerm) {
                const searchLower = searchTerm.toLowerCase();
                const visitorName = log.visited_by_name || log.userName?.name || '';
                if (!visitorName.toLowerCase().includes(searchLower)) return false;
            }

            return true;
        }).sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    }, [logs, quickFilter, dateRange, searchTerm]);

    const columns = [
        {
            dataField: 'created_at',
            text: 'Date & Time',
            sort: true,
            formatter: (cell) => moment(cell).format('DD MMM YYYY, hh:mm A'),
            style: { fontWeight: 'bold' }
        },
        ...(!apiaryId ? [{
            dataField: 'apiaryName',
            text: 'Apiary',
            sort: true,
            formatter: (cell) => cell || 'N/A',
            style: { fontWeight: '500' }
        }] : []),
        {
            dataField: 'visited_by_name',
            text: 'Last Visited By',
            sort: true,
            formatter: (cell, row) => {
                const displayName = row.visited_by_name || row.userName?.name || 'Unknown';
                const role = row.added_by_user_type || (row.visited_by_name ? 'bee_mitra' : 'beekeeper');
                const isBeeMitra = role.toLowerCase() === 'bee_mitra' || Boolean(row.visited_by_name);
                const targetId = row.visited_by_id?.$oid || row.visited_by_id || row.user_id || row.createdBy?._id;
                return (
                    <div>
                        {targetId ? (
                            <a
                                href={`/user-profile/${targetId}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ fontWeight: 600, color: '#2563eb', textDecoration: 'none' }}
                            >
                                {displayName} 🔗
                            </a>
                        ) : (
                            <span style={{ fontWeight: '500' }}>{displayName}</span>
                        )}
                        <span style={{ 
                            marginLeft: '8px', 
                            fontSize: '9px', 
                            padding: '2px 6px', 
                            borderRadius: '4px',
                            background: isBeeMitra ? '#f3e8ff' : '#ffe8d6',
                            color: isBeeMitra ? '#7c3aed' : '#d97706',
                            fontWeight: '700',
                            textTransform: 'uppercase',
                            letterSpacing: '0.3px',
                            display: 'inline-block'
                        }}>
                            {isBeeMitra ? 'Bee Mitra' : 'Beekeeper'}
                        </span>
                    </div>
                );
            }
        },
        {
            dataField: 'observation',
            text: 'Colonies Status',
            formatter: (_, row) => {
                const obs = row.observation;
                return (
                    <div style={{ lineHeight: '1.8' }}>
                        <StatusBadge type="success">S: {obs?.number_of_strong_colonies ?? 0}</StatusBadge>
                        <StatusBadge type="warning">W: {obs?.number_of_weak_colonies ?? 0}</StatusBadge>
                        <StatusBadge type="error">Q-: {obs?.number_of_colonies_without_queen ?? 0}</StatusBadge>
                        <StatusBadge type="info">E: {obs?.number_of_empty_hives ?? 0}</StatusBadge>
                    </div>
                );
            }
        },
        {
            dataField: 'health',
            text: 'Health Issues',
            formatter: (_, row) => {
                const pests = parseArrayField(row.observation?.pests_list);
                const diseases = parseArrayField(row.observation?.diseases_list);
                const issues = [...pests, ...diseases];
                
                if (issues.length > 0) {
                    return (
                        <div style={{ maxWidth: 180, display: 'flex', flexWrap: 'wrap' }}>
                            {issues.slice(0, 2).map((iss, i) => (
                                <StatusBadge key={i} type="error">{iss}</StatusBadge>
                            ))}
                            {issues.length > 2 && <small style={{ color: '#d63031', fontWeight: 'bold', marginLeft: 4 }}>+{issues.length - 2} more</small>}
                        </div>
                    );
                }
                return <StatusBadge type="success">Healthy</StatusBadge>;
            }
        },
        {
            dataField: 'observation.availability_of_flowers',
            text: 'Foraging (Fl / Nc / Po)',
            formatter: (_, row) => {
                const obs = row.observation;
                const fl = obs?.availability_of_flowers || 'N/A';
                const nc = obs?.availability_of_nectar || 'N/A';
                const po = obs?.availability_of_pollen || 'N/A';
                
                return (
                    <div style={{ display: 'flex', gap: '2px' }}>
                        <StatusBadge type={fl} title={`Flowers: ${fl}`}>Fl: {fl}</StatusBadge>
                        <StatusBadge type={nc} title={`Nectar: ${nc}`}>Nc: {nc}</StatusBadge>
                        <StatusBadge type={po} title={`Pollen: ${po}`}>Po: {po}</StatusBadge>
                    </div>
                );
            }
        },
        {
            dataField: 'action',
            text: 'Actions',
            formatter: (_, row) => (
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <Button 
                        variant="link" 
                        size="sm" 
                        onClick={() => setSelectedLog(row)}
                        style={{ color: '#ff8701', textDecoration: 'none', fontWeight: 700, padding: 0 }}
                    >
                        View Details
                    </Button>
                    <span 
                        onClick={() => handleDelete(row)}
                        style={{ cursor: 'pointer', color: '#d63031', fontSize: '15px', padding: '0 5px' }}
                        title="Delete log entry"
                    >
                        <i className="fa fa-trash"></i>
                    </span>
                </div>
            ),
            headerStyle: { width: '150px' }
        }
    ];

    const EXPORT_COLUMNS = [
        { label: 'Date & Time', key: 'created_at', formatter: (row) => row.created_at ? moment(row.created_at).format('DD MMM YYYY, hh:mm A') : '' },
        ...(!apiaryId ? [{ label: 'Apiary', key: 'apiaryName', formatter: (row) => row.apiaryName || 'N/A' }] : []),
        { label: 'Last Visited By', key: 'visitedBy', formatter: (row) => row.visited_by_name || row.userName?.name || 'Unknown' },
        { label: 'Role', key: 'role', formatter: (row) => (row.added_by_user_type || 'beekeeper').toLowerCase() === 'bee_mitra' ? 'Bee Mitra' : 'Beekeeper' },
        { label: 'Strong Colonies', key: 'strong', formatter: (row) => row.observation?.number_of_strong_colonies ?? 0 },
        { label: 'Weak Colonies', key: 'weak', formatter: (row) => row.observation?.number_of_weak_colonies ?? 0 },
        { label: 'Queenless Colonies', key: 'queenless', formatter: (row) => row.observation?.number_of_colonies_without_queen ?? 0 },
        { label: 'Empty Hives', key: 'empty', formatter: (row) => row.observation?.number_of_empty_hives ?? 0 },
        { label: 'Pests', key: 'pests', formatter: (row) => parseArrayField(row.observation?.pests_list).join('; ') },
        { label: 'Diseases', key: 'diseases', formatter: (row) => parseArrayField(row.observation?.diseases_list).join('; ') },
        { label: 'Flowers Availability', key: 'flowers', formatter: (row) => row.observation?.availability_of_flowers || 'N/A' },
        { label: 'Nectar Availability', key: 'nectar', formatter: (row) => row.observation?.availability_of_nectar || 'N/A' },
        { label: 'Pollen Availability', key: 'pollen', formatter: (row) => row.observation?.availability_of_pollen || 'N/A' }
    ];

    return (
        <MainContainer isGlobal={!apiaryId}>
            <HeaderSection>
                <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                    <h2 style={{ margin: 0, color: '#2d3436', fontWeight: 700, fontSize: apiaryId ? '20px' : '28px' }}>
                        {apiaryId ? 'Hive Activity visit logs' : 'Hive Activity Logs'}
                    </h2>
                    <div style={{ background: '#ff8701', color: 'white', padding: '4px 12px', borderRadius: '15px', fontSize: '12px', fontWeight: 700 }}>
                        {logs.length} Logged
                    </div>
                </div>
                <Button
                    variant="outline-secondary"
                    onClick={() => setShowExportModal(true)}
                    style={{ fontWeight: 700, borderRadius: '8px' }}
                >
                    <i className="fa fa-download mr-2"></i> Export CSV
                </Button>
            </HeaderSection>

            {/* Filter Section */}
            <FilterCard>
                <Row className="align-items-end">
                    <Col md={3}>
                        <Form.Label style={{ fontSize: '13px', fontWeight: 600 }}>Quick Filter</Form.Label>
                        <Select 
                            style={{ width: '100%' }} 
                            value={quickFilter} 
                            onChange={v => setQuickFilter(v)}
                        >
                            <Option value="all">All logs</Option>
                            <Option value="queenless">Queenless Colonies Only</Option>
                            <Option value="pests">Pests Present Only</Option>
                            <Option value="diseases">Diseases Present Only</Option>
                        </Select>
                    </Col>
                    <Col md={4}>
                        <Form.Label style={{ fontSize: '13px', fontWeight: 600 }}>Date Range</Form.Label>
                        <DatePicker.RangePicker 
                            style={{ width: '100%' }} 
                            value={dateRange}
                            onChange={dates => setDateRange(dates)} 
                        />
                    </Col>
                    <Col md={3}>
                        <Form.Label style={{ fontSize: '13px', fontWeight: 600 }}>Search Last Visited By</Form.Label>
                        <Form.Control 
                            placeholder="Visited by name..." 
                            value={searchTerm} 
                            onChange={e => setSearchTerm(e.target.value)}
                        />
                    </Col>
                    <Col md={2} className="text-right">
                        <Button 
                            variant="outline-secondary" 
                            style={{ width: '100%', fontWeight: 600, borderRadius: '8px' }}
                            onClick={() => {
                                setQuickFilter('all');
                                setDateRange(null);
                                setSearchTerm('');
                            }}
                        >
                            Reset
                        </Button>
                    </Col>
                </Row>
            </FilterCard>

            <LoadingOverlay
                active={loading}
                spinner={<BounceLoader color={'#ff8701'} />}
                text='Loading logs...'
                styles={{
                    overlay: (base) => ({
                        ...base,
                        background: 'rgba(255, 255, 255, 0.7)',
                        zIndex: 10
                    })
                }}
            >
                {filteredLogs.length === 0 && !loading ? (
                    <Empty 
                        image={Empty.PRESENTED_IMAGE_SIMPLE} 
                        description="No activity logs found" 
                        style={{ padding: '60px 0', background: '#fff', borderRadius: '12px', border: '1px dashed #ddd' }}
                    />
                ) : (
                    <TableWrapper>
                        <BootstrapTable
                            keyField="_id"
                            data={filteredLogs}
                            columns={columns}
                            hover
                            bordered={false}
                            className="modern-table"
                            pagination={paginationFactory({
                                sizePerPage: 25,
                                hideSizePerPage: true,
                                hidePageListOnlyOnePage: true
                            })}
                        />
                    </TableWrapper>
                )}
            </LoadingOverlay>

            {selectedLog && (
                <ActivityLogDetailModal
                    data={selectedLog}
                    onClose={() => setSelectedLog(null)}
                />
            )}

            <GenericExportCSVModal
                show={showExportModal}
                onHide={() => setShowExportModal(false)}
                data={filteredLogs}
                columns={EXPORT_COLUMNS}
                title="Export Hive Activity Logs to CSV"
                filenamePrefix="hive_activity_logs_export"
            />
        </MainContainer>
    );
}
